<template>
  <div
    class="vs-auto-suggest"
    :dir="direction"
    :class="{
      'vs-has-eyebrow': eyebrow === 'true',
      'vs-has-dropdown': dropdown === 'true',
    }"
  >
    <label
      :id="'label-' + id"
      :dir="direction"
      class="vs-label"
      :class="{
        'vs-label-eyebrow': eyebrow === 'true',
        'vs-has-focus': inputElFocus,
      }"
      :for="'autosuggestinput_' + id"
    >
      {{ label }}
    </label>
    <div
      :id="'combobox-' + id"
      :dir="direction"
      class="vs-input-group vs-inline-group"
      :class="{ 'vs-eyebrow-group': eyebrow === 'true' }"
      @focusout="onblur_input"
    >
      <input
        :id="'autosuggestinput_' + id"
        v-model="searchValue"
        :dir="direction"
        aria-autocomplete="list"
        role="combobox"
        aria-expanded="false"
        :aria-owns="'listbox-' + id"
        aria-haspopup="listbox"
        class="vs-input vs-suggest-text"
        :class="{ 'vs-error': showError && !valid }"
        type="text"
        :aria-required="isRequired"
        :aria-describedby="showError && !valid ? inputAllyAlert : ''"
        :aria-controls="ariaControlsId"
        @keydown="onkeydown_input($event)"
        @keydown.tab="ontab_input()"
        @focus="onfocus_input()"
      >
      <button
        v-if="submitButtonLabel && submitButtonIcon === 'true'"
        class="vs-btn vs-btn-submit-icon"
        @click="onclick_submit()"
      >
        <span class="visa-icon visa-icon-search-md">
          <span class="vs-sr-only">{{ submitButtonLabel }}</span>
        </span>
      </button>
      <button
        v-if="submitButtonLabel && submitButtonIcon === ''"
        class="vs-btn vs-btn-primary vs-btn-submit"
        @click="onclick_submit()"
      >
        {{ submitButtonLabel }}
      </button>
      <button
        v-if="dropdown === 'true' && !showClearButton"
        class="vs-btn vs-btn-dropdown-icon"
        tabindex="-1"
        type="button"
        @click="onclick_dropdown()"
      >
        <span class="visa-icon visa-icon-arrow-expand-sm vs-select-icon">
          <span class="vs-sr-only">{{
            showSuggestList ? dropdownButtonCloseLabel : dropdownButtonOpenLabel
          }}</span>
        </span>
      </button>
      <button
        v-if="clearButtonLabel && dropdown === 'true' && showClearButton"
        class="vs-btn vs-btn-dropdown-icon"
        type="button"
        @click="onclick_clear()"
      >
        <span class="visa-icon visa-icon-close-md">
          <span class="vs-sr-only">{{ clearButtonLabel }}</span>
        </span>
      </button>
    </div>
    <template v-if="asResults && showSuggestList">
      <ul
        v-if="JSON.parse(asResults).length > 0"
        :id="'listbox-' + id"
        :aria-labelledby="'label-' + id"
        role="listbox"
        class="vs-listbox vs-text"
        tabindex="-1"
      >
        <template v-if="showFeatured && featuredLabel && nonFeaturedLabel">
          <h4 class="vs-lead">
            {{ featuredLabel }}
          </h4>
          <li
            v-for="(item, index) in JSON.parse(featured)"
            :id="'listbox-item-' + index"
            :key="index"
            :data-key="JSON.stringify(item)"
            role="option"
            tabindex="-1"
            @click="onclick_selectItem($event)"
            v-html="item.description"
          />
          <h4 class="vs-lead">
            {{ nonFeaturedLabel }}
          </h4>
        </template>
        <li
          v-for="(item, index) in JSON.parse(asResults)"
          :id="'listbox-item-' + (index + JSON.parse(featured).length)"
          :key="index + JSON.parse(featured).length"
          :data-key="JSON.stringify(item)"
          role="option"
          tabindex="-1"
          @click="onclick_selectItem($event)"
          v-html="item.description"
        >
          >
        </li>
      </ul>
      <div
        role="status"
        aria-live="assertive"
        aria-relevant="text"
        class="vs-sr-only"
      />
    </template>
    <InlineAlert
      v-if="showError && !valid && errorMessage"
      :id="inputAllyAlert"
      :message="errorMessage"
    />
  </div>
</template>

<script>
import InlineAlert from '../../../inlineAlert/InlineAlert.vue';
import { generateId } from '@/helpers/generate-id';
import { isMobile } from '@/helpers/breakpoints';
import { setDirection } from '@/helpers/direction';

export default {
  name: 'VsAutoSuggestInput',
  expose: ['resetValue', 'setValue'],
  props: {
    id: { type: String, default: generateId().toString() },
    label: { type: String, default: '' },
    eyebrow: { type: String, default: '' },
    dropdown: { type: String, default: '' },
    isRequired: { type: String, default: 'false' },
    clearOnInput: { type: Boolean, default: false },
    featured: { type: String, default: '[]' },
    defaultItemIndex: { type: String, default: '' },
    featuredLabel: { type: String, default: '' },
    nonFeaturedLabel: { type: String, default: '' },
    submitButtonLabel: { type: String, default: '' },
    submitButtonIcon: { type: String, default: '' },
    clearButtonLabel: { type: String, default: '' },
    dropdownButtonOpenLabel: { type: String, default: '' },
    dropdownButtonCloseLabel: { type: String, default: '' },
    asResults: { type: String, default: '[]' },
    value: { type: String },
    errorMsg: { type: String },
    validators: { type: Array, default: null },
    direction: { type: String, default: setDirection() },
  },
  components: {
    InlineAlert,
  },
  data() {
    return {
      suggestions: null,
      inputAllyAlert: 'input_alert_' + this.id,
      inputEl: null,
      inputElFocus: false,
      comboxContainer: null,
      activeItemIndex: -1,
      asResultsArray: null,
      adaMessage: null,
      errorMessage: null,
      showError: false,
      showSuggestList: false,
      showFeatured: false,
      showClearButton: false,
      touched: false,
      valid: true,
      resultCount: 0,
    };
  },
  computed: {
    searchValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    ariaControlsId: {
      get() {
        return this.asResults && this.showSuggestList
          ? 'listbox-' + this.id
          : null;
      },
    },
  },
  watch: {
    searchValue() {
      if (this.searchValue.length < 1) {
        this.showFeatured = true;
        this.showClearButton = false;
        if (!this.dropdown) {
          this.hideSuggestionList();
        } else if (this.dropdown && this.touched) {
          this.showSuggestionList();
        }
        this.emitKeyupEvent();
      } else if (this.touched) {
        this.showFeatured = false;
        this.showClearButton = true;
        this.emitKeyupEvent();
        if (!this.dropdown) {
          this.showSuggestionList();
        }
      }
      if (this.eyebrow) {
        this.inputElFocus = !!this.searchValue;
      }
    },
  },
  mounted() {
    this.inputEl = this.$el.querySelector('input');
    this.listEl = this.$el.querySelector('ul');
    this.comboxContainer = this.$el.querySelector('div input[role=combobox]');
    this.onInit();
  },
  updated() {
    this.$nextTick(() => {
      if (this.asResults) {
        this.asResultsArray = this.getResultsArray();
        if (this.resultCount == 1) {
          let adaMessage = '';
          var element = this.$el.querySelector('div[role=status]');
          if (element) {
            let resultLength = this.getResultsArray().length;
            if (this.searchValue.length % 2 != 0)
              adaMessage =
                resultLength +
                ' results are available, use up and down arrow keys to navigate.';
            else
              adaMessage =
                resultLength +
                ' results are available, use up and down arrow keys to navigate';
            this.updateAdaMessage(adaMessage);
          }
        }
        this.resultCount = 1;
      }
    });
  },
  methods: {
    onInit() {
      this.showFeatured =
        this.featuredLabel &&
        this.nonFeaturedLabel &&
        JSON.parse(this.featured).length;
      if (this.asResults) {
        this.asResultsArray = this.getResultsArray();
        const index = parseInt(this.defaultItemIndex, 10);
        if (!isNaN(index) && index >= 0) {
          this.activeItemIndex = index;
          this.searchValue = this.asResultsArray[index].description;
        } else {
          this.activeItemIndex = -1;
          this.searchValue = '';
        }
      }
      this.$nextTick(() => {
        this.handleChange(this.searchValue);
      });
    },
    onkeydown_input(e) {
      this.resultCount = 0;
      const key = e.code;
      switch (key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'Escape':
        case 'Tab':
        case 'Backspace':
          this.handleKeydown(key);
          return;
        case 'Enter':
          e.preventDefault();
          this.handleKeydown(key);
          return;
        default:
          if (!this.touched) {
            this.onfocus_input();
          }
          return;
      }
    },
    ontab_input() {
      this.handleKeydown('Tab');
    },
    onblur_select_optn() {
      if (this.searchValue) {
        const elem = this.$el.querySelector('ul li');
        const searchedResults = this.getResultsArray() || [];
        const searchResult = searchedResults.find(
          (searchObject) => searchObject.key === this.searchValue.toUpperCase()
        );
        if (searchResult) {
          if (elem) {
            this.searchValue = elem.innerText;
            this.setSelectedItem(elem);
            this.onselect_dispatch(searchResult.key);
            this.handleChange(searchResult.key);
          }
        }
      }
    },
    onblur_input(event) {
      const self = this;
      const isFocusedOutsideComponent =
        !event.relatedTarget || !self.$el.contains(event.relatedTarget);
      if (isFocusedOutsideComponent) {
        self.hideSuggestionList();
        if (self.eyebrow) {
          self.inputElFocus = !!self.searchValue;
        }
        this.handleChange(this.searchValue?.toLowerCase());
        if (this.valid) {
          self.onblur_select_optn();
        }
      }
    },
    onfocus_input() {
      this.showError = false;
      if (this.eyebrow) {
        this.inputElFocus = true;
      }
      if (this.clearOnInput) {
        this.searchValue = '';
      }
      if (this.dropdown) {
        this.showSuggestionList();
      }
      this.touched = true;
    },
    emitKeyupEvent() {
      new CustomEvent('keyup_auto_suggest');
      this.$emit('keyup_auto_suggest', this.searchValue);
    },
    handleKeydown(keyCode) {
      if (keyCode === 'Escape') {
        this.onclick_clear();
        return;
      }

      if (!this.asResults) {
        return;
      }

      let listItems = this.$el.querySelectorAll('ul li');
      let item = null;
      switch (keyCode) {
        case 'ArrowUp':
          if (this.activeItemIndex <= 0) {
            this.activeItemIndex = this.asResultsArray.length - 1;
          } else {
            this.activeItemIndex--;
          }
          item = listItems[this.activeItemIndex];
          if (item)
            item.scrollIntoView({
              behavior: 'auto',
              block: 'nearest',
              inline: 'start',
            });
          break;
        case 'ArrowDown':
          if (
            this.activeItemIndex === -1 ||
            this.activeItemIndex >= this.asResultsArray.length - 1
          ) {
            this.activeItemIndex = 0;
          } else {
            this.activeItemIndex++;
          }
          item = listItems[this.activeItemIndex];
          if (item)
            item.scrollIntoView({
              behavior: 'auto',
              block: 'nearest',
              inline: 'start',
            });
          break;
        case 'Enter': {
          const elem = this.$el.querySelectorAll('ul li');
          let selectedElement = undefined;
          if (
            this.activeItemIndex >= 0 &&
            elem[this.activeItemIndex] &&
            elem[this.activeItemIndex].innerText
          ) {
            selectedElement = elem[this.activeItemIndex];
          } else {
            selectedElement = elem[0];
          }
          if (selectedElement) {
            this.setSelectedItem(selectedElement);
            this.onclick_submit(selectedElement.innerText);
          } else {
            this.onclick_submit(this.searchValue);
          }
          this.$nextTick(() => {
            this.hideSuggestionList();
          });
          return;
        }
        case 'Tab':
          if (this.activeItemIndex < 0) {
            return;
          }
          if (this.showSuggestList) {
            const liElements =
              Array.from(this.$el.querySelectorAll('ul li')) || [];
            if (liElements.indexOf(this.activeItemIndex) !== -1) {
              this.searchValue = liElements[this.activeItemIndex].innerText;
            }
          }
          return;
        default:
          return;
      }

      if (listItems && listItems[this.activeItemIndex])
        this.setSelectedItem(listItems[this.activeItemIndex]);
    },
    onclick_selectItem(e) {
      const selectedItem =
        e.composedPath && e.composedPath()
          ? e
              .composedPath()
              .find((el) => el.id && el.id.startsWith('listbox-item'))
          : e.target;
      const selectedValue = selectedItem.innerText;
      this.searchValue = selectedValue;
      this.setSelectedItem(selectedItem);
      this.onselect_dispatch(selectedItem.dataset.key);
      this.$nextTick(() => {
        this.hideSuggestionList();
        this.handleChange(this.searchValue);
      });
    },
    setSelectedItem(item) {
      this.inputEl.setAttribute('data-selected', item.dataset.key);
      const allItems = this.$el.querySelectorAll('ul li');
      for (let i = 0; i < allItems.length; i++) {
        allItems[i].setAttribute('aria-selected', 'false');
        allItems[i].classList.remove('focused');
      }
      item.setAttribute('aria-selected', 'true');
      item.classList.add('focused');
      const selectedItemId = item.getAttribute('id');
      this.inputEl.setAttribute('aria-activedescendant', selectedItemId);
      this.updateAdaMessage(item.innerText);
    },
    onselect_dispatch(data) {
      new CustomEvent('select_option_auto_suggest');
      this.$emit('select_option_auto_suggest', data);
    },
    onclick_submit(submitValue) {
      if (submitValue) {
        this.searchValue = submitValue;
      }
      const detail = {
        searchQuery: submitValue ? submitValue : this.searchValue,
        searchObject: this.inputEl.dataset.selected,
      };
      new CustomEvent('click_button_auto_suggest');
      this.$emit('click_button_auto_suggest', detail);
    },
    onclick_clear() {
      this.$emit('reset_auto_suggest', this.searchValue);
      this.resetValue();
    },
    hideSuggestionList() {
      this.comboxContainer.setAttribute('aria-expanded', 'false');
      this.inputEl.removeAttribute('aria-activedescendant');
      this.activeItemIndex = -1;
      this.suggestions = null;
      this.showSuggestList = false;
    },
    showSuggestionList() {
      this.showSuggestList = true;
      this.comboxContainer.setAttribute('aria-expanded', 'true');
      if (isMobile()) {
        setTimeout(() => {
          window.scrollTo(
            0,
            this.$el.getBoundingClientRect().top + window.pageYOffset - 10
          );
        }, 0);
      }
    },
    updateAdaMessage(message) {
      var element = this.$el.querySelector('div[role=status]');
      if (element) element.textContent = message;
    },
    onclick_dropdown() {
      this.touched = true;
      if (!this.showSuggestList) {
        this.inputEl.focus();
        this.showSuggestionList();
      } else {
        this.hideSuggestionList();
      }
    },
    getResultsArray() {
      return this.showFeatured && this.featured && this.asResults
        ? JSON.parse(this.featured).concat(JSON.parse(this.asResults))
        : JSON.parse(this.asResults);
    },
    resetValue() {
      this.touched = false;
      this.showClearButton = false;
      this.$nextTick(() => {
        this.onInit();
      });
    },
    setValue(newValue) {
      this.touched = true;
      this.searchValue = newValue;
      this.$nextTick(() => {
        this.hideSuggestionList();
        this.handleChange(this.searchValue);
      });
    },
    checkValidations(value) {
      if (!this.validators) {
        this.valid = true;
        return;
      }
      let result = true;

      this.validators.forEach((validator) => {
        if (!validator(value)) {
          result = false;
        }
      });
      this.valid = result;
      if (this.valid || !this.touched) {
        this.errorMessage = '';
        this.showError = false;
      } else {
        this.errorMessage = this.errorMsg;
        this.showError = true;
      }
    },
    handleChange(value) {
      this.checkValidations(value);
      this.$emit('onChange', this.valid);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './autoSuggestInput';
@import '@/styles/core/types/types';
@import '@/styles/core/types/icons';
@import '@/styles/buttons/buttons';
@import '@/styles/input';
@import '@/styles/label';
@import '@/styles/accessibility';
</style>
